(() => {
  if (window.__instafeedFeedControllerLoaded) return;
  window.__instafeedFeedControllerLoaded = true;

  const FEED_SELECTOR = '#insta-feed';
  const APP_SRC_PATTERN = /https:\\\/\\\/cdn\.nfcube\.com\\\/(instafeed-[^"'\\]+\?shop=[^"'\\]+)/i;
  const FEED_ITEM_SELECTOR = '.instafeed-new-layout-item';
  const FEED_STRUCTURE_SELECTOR = [
    FEED_ITEM_SELECTOR,
    '.instafeed-new-layout-container',
    '.instafeed-new-layout-wrapper',
  ].join(',');
  const FEED_ATTRIBUTE_FILTER = [
    'aria-label',
    'class',
    'data-feed-id',
    'data-feed-type',
    'data-media-type',
    'data-post-type',
    'data-type',
    'href',
    'src',
    'title',
  ];
  const MEDIA_SELECTOR = [
    ':scope > video',
    ':scope > img',
    '.instafeed-new-layout-item-link video',
    '.instafeed-new-layout-item-link img',
    '.instafeed-new-layout-item-container video',
    '.instafeed-new-layout-item-container img',
    'video',
    'img',
  ].join(',');
  const FEED_KINDS = ['image', 'video'];
  const FIXED_FEED_KIND = 'video';
  const MAX_VISIBLE_ITEMS = 4;
  const MAX_RETRIES = 4;
  const RETRY_BASE_DELAY_MS = 1500;
  const MUTATION_SETTLE_DELAY_MS = 50;
  const POST_LOAD_CHECK_DELAY_MS = 250;
  const PLACEHOLDER_IMAGE_PATTERN = /placeholder\.png/i;

  const rootStateCache = new WeakMap();
  const itemStateCache = new WeakMap();

  let retryCount = 0;
  let retryTimer = null;
  let observer = null;
  let observedRoot = null;
  let appScriptUrl = null;
  let normalizeTimer = null;
  let normalizeFrame = null;
  let normalizeDueAt = 0;
  let pendingNormalizeRoot = null;
  let activeKind = FIXED_FEED_KIND;
  let instafeedInitializedRoot = null;

  function getFeedRoot() {
    return document.querySelector(FEED_SELECTOR);
  }

  function getFeedScope(root) {
    return root?.closest('.shopify-section') || root?.parentElement || root || document;
  }

  function getRootState(root) {
    let state = rootStateCache.get(root);

    if (!state) {
      state = {
        scope: getFeedScope(root),
        layoutContainer: null,
        layoutWrapper: null,
        toggle: null,
        toggleButtons: [],
        emptyState: null,
        items: [],
      };
      rootStateCache.set(root, state);
    }

    return state;
  }

  function refreshRootState(root) {
    const state = getRootState(root);

    state.scope = getFeedScope(root);
    state.layoutContainer = root.querySelector('.instafeed-new-layout-container');
    state.layoutWrapper = root.querySelector('.instafeed-new-layout-wrapper');
    state.items = Array.from(root.querySelectorAll(FEED_ITEM_SELECTOR));

    if (!state.toggle || !state.scope.contains(state.toggle)) {
      state.toggle = state.scope.querySelector('.instafeed-kind-toggle');
      state.toggleButtons = state.toggle ? Array.from(state.toggle.querySelectorAll('.instafeed-kind-toggle__button')) : [];
    }

    if (!state.emptyState || !root.contains(state.emptyState)) {
      state.emptyState = state.layoutWrapper?.querySelector(':scope > .instafeed-empty-state') || null;
    }

    return state;
  }

  function hasFeedItems(root = getFeedRoot()) {
    return Boolean(root?.querySelector(FEED_ITEM_SELECTOR));
  }

  function findExistingAppScript() {
    return Array.from(document.scripts).find((script) => /cdn\.nfcube\.com\/instafeed-/i.test(script.src));
  }

  function resolveAppScriptUrl() {
    if (appScriptUrl) return appScriptUrl;

    const existingScript = findExistingAppScript();
    if (existingScript?.src) {
      appScriptUrl = existingScript.src;
      return appScriptUrl;
    }

    for (const script of document.scripts) {
      const content = script.textContent || '';
      const match = content.match(APP_SRC_PATTERN);

      if (match) {
        appScriptUrl = `https://cdn.nfcube.com/${match[1]}`.replace(/\\\//g, '/');
        return appScriptUrl;
      }
    }

    return null;
  }

  function clearRetryTimer() {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  }

  function clearNormalizeTimer() {
    if (normalizeTimer) {
      clearTimeout(normalizeTimer);
      normalizeTimer = null;
    }

    if (normalizeFrame) {
      cancelAnimationFrame(normalizeFrame);
      normalizeFrame = null;
    }

    normalizeDueAt = 0;
  }

  function hydrateImageMedia(media) {
    if (!media || media.tagName !== 'IMG') return;

    const placeholderSrc = media.currentSrc || media.getAttribute('src') || '';
    const actualSrc = media.dataset.instafeedSrc;

    if (actualSrc && PLACEHOLDER_IMAGE_PATTERN.test(placeholderSrc)) {
      media.src = actualSrc;
    }
  }

  function getFeedItemMedia(item, itemState = itemStateCache.get(item)) {
    if (!item) return null;

    if (itemState?.media && item.contains(itemState.media)) {
      return itemState.media;
    }

    return item.querySelector(MEDIA_SELECTOR);
  }

  function getAttributeSignature(element, attributes) {
    if (!element) return '';

    return attributes
      .map((attribute) => {
        if (attribute === 'class') return element.className;
        if (attribute === 'href') return element.getAttribute('href');
        return element.dataset?.[attribute] || element.getAttribute(`data-${attribute}`);
      })
      .filter(Boolean)
      .join(' ');
  }

  function getDatasetSignature(element) {
    if (!element?.dataset) return '';

    return Object.entries(element.dataset)
      .map(([key, value]) => `${key} ${value}`)
      .join(' ');
  }

  function getDescendantSignature(item) {
    const hintedElements = item.querySelectorAll(
      [
        'a[href]',
        '[data-feed-id]',
        '[data-feed-type]',
        '[data-media-type]',
        '[data-type]',
        '[data-post-type]',
        '[aria-label]',
        '[title]',
        '[class*="video" i]',
        '[class*="reel" i]',
      ].join(',')
    );

    return Array.from(hintedElements)
      .map((element) => {
        return [
          getDatasetSignature(element),
          element.className,
          element.getAttribute('href'),
          element.getAttribute('aria-label'),
          element.getAttribute('title'),
        ]
          .filter(Boolean)
          .join(' ');
      })
      .join(' ');
  }

  function getFeedItemKind(item, media) {
    const link = item.querySelector('a[href]');
    const feedIdElement = item.querySelector('[data-feed-id], [data-feed-type], [data-media-type], [data-type]');
    const hoverLayer = item.querySelector('.instafeed-hover-layer');
    const signature = [
      getDatasetSignature(item),
      getAttributeSignature(item, ['feedId', 'feedType', 'mediaType', 'type', 'postType', 'class']),
      getAttributeSignature(feedIdElement, ['feedId', 'feedType', 'mediaType', 'type', 'postType', 'class']),
      getAttributeSignature(hoverLayer, ['feedId', 'feedType', 'mediaType', 'type', 'postType', 'class']),
      getAttributeSignature(link, ['href']),
      getDescendantSignature(item),
      media?.tagName,
      media?.getAttribute('src'),
      media?.getAttribute('data-src'),
      media?.getAttribute('data-instafeed-src'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (
      item.querySelector('video, source') ||
      media?.tagName === 'VIDEO' ||
      /\b(video|videos|reel|reels|mp4|webm|mov)\b/i.test(signature) ||
      /\/reels?\//i.test(signature)
    ) {
      return 'video';
    }

    if (media?.tagName === 'IMG' || item.querySelector('img')) {
      return 'image';
    }

    return '';
  }

  function getKindLabel(kind) {
    return kind === 'video' ? 'Reels' : 'Posts';
  }

  function getStoredActiveKind(root) {
    activeKind = FIXED_FEED_KIND;
    root.dataset.instafeedActiveKind = FIXED_FEED_KIND;
    return activeKind;
  }

  function setAttributeIfChanged(element, attribute, value) {
    if (element.getAttribute(attribute) !== value) {
      element.setAttribute(attribute, value);
    }
  }

  function setBooleanDataset(element, name, value) {
    const nextValue = String(value);

    if (element.dataset[name] !== nextValue) {
      element.dataset[name] = nextValue;
    }
  }

  function setHidden(element, value) {
    if (element.hidden !== value) {
      element.hidden = value;
    }
  }

  function setItemVisibility(item, isVisible) {
    setHidden(item, !isVisible);

    if (isVisible) {
      item.style.setProperty('display', 'block', 'important');
      item.style.removeProperty('visibility');
      item.style.removeProperty('opacity');
      return;
    }

    item.style.setProperty('display', 'none', 'important');
  }

  function setActiveKind(root, kind) {
    if (!FEED_KINDS.includes(kind)) return;

    activeKind = kind;
    root.dataset.instafeedActiveKind = kind;

    const state = getRootState(root);
    state.toggleButtons.forEach((button) => {
      const isActive = button.dataset.instafeedKind === kind;
      const nextTabIndex = isActive ? 0 : -1;

      setAttributeIfChanged(button, 'aria-pressed', String(isActive));
      setAttributeIfChanged(button, 'aria-selected', String(isActive));

      if (button.tabIndex !== nextTabIndex) {
        button.tabIndex = nextTabIndex;
      }
    });
  }

  function getEmptyState(state) {
    if (!state.layoutWrapper) return null;

    if (state.emptyState && state.layoutWrapper.contains(state.emptyState)) {
      return state.emptyState;
    }

    const emptyState = document.createElement('div');
    emptyState.className = 'instafeed-empty-state';
    emptyState.setAttribute('role', 'status');
    emptyState.setAttribute('aria-live', 'polite');
    state.layoutWrapper.appendChild(emptyState);
    state.emptyState = emptyState;

    return emptyState;
  }

  function getAvailableKinds(items) {
    return FEED_KINDS.filter((kind) => items.some((item) => item.dataset.instafeedKind === kind));
  }

  function cleanupItemState(item) {
    if (item.hasAttribute('data-instafeed-featured')) {
      item.removeAttribute('data-instafeed-featured');
    }

    if (item.hasAttribute('data-instafeed-group-label')) {
      item.removeAttribute('data-instafeed-group-label');
    }
  }

  function normalizeFeedShell(state) {
    [state.layoutContainer, state.layoutWrapper].forEach((element) => {
      if (!element) return;

      element.style.removeProperty('height');
      element.style.removeProperty('max-height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('overflow-x');
      element.style.removeProperty('overflow-y');
    });
  }

  function getSelectedItems(items, selectedKind, shouldFilter) {
    return items.filter((item) => !shouldFilter || item.dataset.instafeedKind === selectedKind);
  }

  function updateVisibleItems(root, items, availableKinds, options = {}) {
    const shouldFilter = true;
    let selectedKind = getStoredActiveKind(root);
    let visibleCount = 0;

    setActiveKind(root, selectedKind);

    const selectedItems = getSelectedItems(items, selectedKind, shouldFilter);

    items.forEach((item) => {
      const matchesSelectedKind = !shouldFilter || item.dataset.instafeedKind === selectedKind;
      const isVisible = matchesSelectedKind && visibleCount < MAX_VISIBLE_ITEMS;
      const isOverflow = matchesSelectedKind && !isVisible;

      setItemVisibility(item, isVisible);

      if (isOverflow) {
        setBooleanDataset(item, 'instafeedOverflow', true);
      } else if (item.dataset.instafeedOverflow) {
        delete item.dataset.instafeedOverflow;
      }

      if (isVisible) {
        visibleCount += 1;
      }
    });

    setBooleanDataset(root, 'instafeedSliderReady', false);
    root.dataset.instafeedSelectedCount = String(selectedItems.length);
    root.dataset.instafeedVisibleCount = String(visibleCount);

    if (options.resetScroll) {
      const state = getRootState(root);

      if (state.layoutWrapper) {
        state.layoutWrapper.scrollLeft = 0;
      }
    }

    return {
      selectedCount: selectedItems.length,
      visibleCount,
    };
  }

  function findFeedTitle(root, state) {
    const matchesTitleText = (element) => {
      if (element.closest('.instafeed-kind-toggle')) return false;

      return element.textContent.trim().replace(/\s+/g, ' ') === 'The Latest From Us';
    };

    return (
      Array.from(state.scope.querySelectorAll('h1, h2, h3, h4')).find(matchesTitleText) ||
      Array.from(state.scope.querySelectorAll('[class*="title"], [class*="heading"]')).find((element) => {
        return matchesTitleText(element) && element.children.length <= 1;
      })
    );
  }

  function placeFeedToggle(root, state, toggle) {
    const title = findFeedTitle(root, state);

    if (title) {
      let titleRow = title.closest('.instafeed-title-row');

      if (!titleRow) {
        titleRow = document.createElement('div');
        titleRow.className = 'instafeed-title-row';
        title.parentNode.insertBefore(titleRow, title);
        titleRow.appendChild(title);
      }

      if (toggle.parentElement !== titleRow) {
        titleRow.appendChild(toggle);
      }

      toggle.dataset.instafeedPlaced = 'title';
      return;
    }

    if (toggle.dataset.instafeedPlaced === 'true' && toggle.isConnected) return;

    if (state.layoutWrapper) {
      state.layoutWrapper.prepend(toggle);
      toggle.dataset.instafeedPlaced = 'true';
      return;
    }

    root.before(toggle);
    toggle.dataset.instafeedPlaced = 'true';
  }

  function createFeedToggle() {
    const toggle = document.createElement('div');
    toggle.className = 'instafeed-kind-toggle';
    toggle.setAttribute('aria-label', 'Instagram feed tabs');
    toggle.setAttribute('role', 'tablist');

    FEED_KINDS.forEach((kind) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'instafeed-kind-toggle__button';
      button.dataset.instafeedKind = kind;
      button.textContent = getKindLabel(kind);
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('role', 'tab');
      button.tabIndex = -1;
      toggle.appendChild(button);
    });

    return toggle;
  }

  function updateFeedToggle(root, state, availableKinds) {
    if (state.toggle) {
      state.toggle.remove();
      state.toggle = null;
      state.toggleButtons = [];
    }
  }

  function normalizeFeedItem(item) {
    if (!item) return;

    let itemState = itemStateCache.get(item);
    const wasNormalized = itemState?.normalized === true;
    const media = getFeedItemMedia(item, itemState);

    if (!itemState) {
      itemState = {};
      itemStateCache.set(item, itemState);
    }

    itemState.media = media;

    itemState.kind = getFeedItemKind(item, media);

    if (item.dataset.instafeedKind !== itemState.kind) {
      item.dataset.instafeedKind = itemState.kind;
    }

    if (!wasNormalized) {
      cleanupItemState(item);
    }

    if (media) {
      if (!media.classList.contains('instafeed-primary-media')) {
        media.classList.add('instafeed-primary-media');
      }

      if (media.tagName === 'IMG') {
        hydrateImageMedia(media);
      }
    }

    itemState.normalized = Boolean(itemState.kind);
  }

  function updateEmptyState(root, state, itemCount, visibleCount) {
    const emptyState = getEmptyState(state);
    if (!emptyState) return;

    const label = getKindLabel(getStoredActiveKind(root));
    const showEmptyState = itemCount > 0 && visibleCount === 0;
    const message = `No ${label.toLowerCase()} available right now.`;

    if (emptyState.textContent !== message) {
      emptyState.textContent = message;
    }

    setBooleanDataset(emptyState, 'visible', showEmptyState);
    setHidden(emptyState, !showEmptyState);
  }

  function applyFeedFilter(root = getFeedRoot(), options = {}) {
    if (!root) return 0;

    const state = getRootState(root);
    const items = state.items.length ? state.items : Array.from(root.querySelectorAll(FEED_ITEM_SELECTOR));
    const availableKinds = getAvailableKinds(items);
    const visibility = updateVisibleItems(root, items, availableKinds, options);

    updateEmptyState(root, state, items.length, visibility.visibleCount);

    return visibility.visibleCount;
  }

  function normalizeFeedLayout(root = getFeedRoot()) {
    if (!root) return;

    clearNormalizeTimer();

    const state = refreshRootState(root);
    normalizeFeedShell(state);

    state.items.forEach((item) => {
      normalizeFeedItem(item);
    });

    const availableKinds = getAvailableKinds(state.items);
    const shouldShowToggle = true;

    setBooleanDataset(root, 'instafeedFilterReady', shouldShowToggle);
    updateFeedToggle(root, state, availableKinds);

    const visibility = updateVisibleItems(root, state.items, availableKinds);
    updateEmptyState(root, state, state.items.length, visibility.visibleCount);

    if (state.items.length > 0) {
      markLoaded(root);
    }
  }

  function flushScheduledNormalization() {
    const root = pendingNormalizeRoot || getFeedRoot();

    normalizeTimer = null;
    normalizeFrame = null;
    normalizeDueAt = 0;
    pendingNormalizeRoot = null;

    normalizeFeedLayout(root);
  }

  function scheduleLayoutNormalization(root = getFeedRoot(), delay = 0) {
    if (!root) return;

    const nextDueAt = performance.now() + delay;

    pendingNormalizeRoot = root;

    if (normalizeDueAt && nextDueAt >= normalizeDueAt) {
      return;
    }

    clearNormalizeTimer();
    normalizeDueAt = nextDueAt;

    if (delay > 0) {
      normalizeTimer = window.setTimeout(flushScheduledNormalization, delay);
      return;
    }

    normalizeFrame = requestAnimationFrame(flushScheduledNormalization);
  }

  function markLoaded(root = getFeedRoot()) {
    if (!root) return;

    if (root.dataset.instafeedState !== 'loaded') {
      root.dataset.instafeedState = 'loaded';
    }

    clearRetryTimer();
  }

  function setState(state) {
    const root = getFeedRoot();
    if (!root || root.dataset.instafeedState === state) return;

    root.dataset.instafeedState = state;
  }

  function runInstafeed() {
    if (typeof window.instafeedApp !== 'function') return false;

    const root = getFeedRoot();
    if (root && instafeedInitializedRoot === root) return true;

    try {
      window.instafeedApp();
      instafeedInitializedRoot = root;
      scheduleLayoutNormalization(root, POST_LOAD_CHECK_DELAY_MS);
      return true;
    } catch (error) {
      console.warn('Instafeed retry failed to initialize.', error);
      return false;
    }
  }

  function attachScriptListeners(script) {
    if (!script || script.dataset.instafeedRetryBound === 'true') return;

    script.dataset.instafeedRetryBound = 'true';

    script.addEventListener(
      'load',
      () => {
        retryCount = 0;
        setState('loading');
        runInstafeed();
        scheduleRetry('script-load');
      },
      { once: true }
    );

    script.addEventListener(
      'error',
      () => {
        scheduleRetry('script-error');
      },
      { once: true }
    );
  }

  function ensureAppScript() {
    const existingScript = findExistingAppScript();
    if (existingScript) {
      attachScriptListeners(existingScript);
      return true;
    }

    const scriptUrl = resolveAppScriptUrl();
    if (!scriptUrl) return false;

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.dataset.instafeedRetry = 'true';
    attachScriptListeners(script);
    document.head.appendChild(script);
    return true;
  }

  function scheduleRetry(reason) {
    const root = getFeedRoot();
    if (!root || hasFeedItems(root)) {
      scheduleLayoutNormalization(root);
      return;
    }

    if (retryCount >= MAX_RETRIES) {
      setState('unavailable');
      return;
    }

    clearRetryTimer();
    setState('loading');

    const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
    retryCount += 1;

    retryTimer = window.setTimeout(() => {
      attemptRecovery(reason);
    }, delay);
  }

  function attemptRecovery(reason) {
    const root = getFeedRoot();
    if (!root || hasFeedItems(root)) {
      scheduleLayoutNormalization(root);
      return;
    }

    const scriptReady = ensureAppScript();
    const initialized = runInstafeed();

    if (!scriptReady && !initialized) {
      scheduleRetry(`${reason}-missing-script`);
      return;
    }

    window.setTimeout(() => {
      if (hasFeedItems(root)) {
        scheduleLayoutNormalization(root);
        return;
      }

      scheduleRetry(`${reason}-still-empty`);
    }, POST_LOAD_CHECK_DELAY_MS);
  }

  function nodeNeedsLayout(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.matches('.instafeed-kind-toggle, .instafeed-kind-toggle *, .instafeed-empty-state')) return false;
    if (node.matches(FEED_STRUCTURE_SELECTOR)) return true;

    return Boolean(node.querySelector?.(FEED_STRUCTURE_SELECTOR));
  }

  function mutationsNeedLayout(mutations) {
    return mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return Boolean(mutation.target.closest?.(FEED_ITEM_SELECTOR));
      }

      return Array.from(mutation.addedNodes).some(nodeNeedsLayout) || Array.from(mutation.removedNodes).some(nodeNeedsLayout);
    });
  }

  function watchFeedRoot() {
    const root = getFeedRoot();
    if (!root) return;

    if (observer && observedRoot === root) return;

    if (observer) {
      observer.disconnect();
      observer = null;
      observedRoot = null;
    }

    observer = new MutationObserver((mutations) => {
      if (!mutationsNeedLayout(mutations)) return;

      scheduleLayoutNormalization(root, MUTATION_SETTLE_DELAY_MS);
    });

    observer.observe(root, {
      attributeFilter: FEED_ATTRIBUTE_FILTER,
      attributes: true,
      childList: true,
      subtree: true,
    });

    observedRoot = root;
  }

  function initInstafeedRecovery() {
    const root = getFeedRoot();
    if (!root) return;

    watchFeedRoot();

    if (hasFeedItems(root)) {
      scheduleLayoutNormalization(root);
      return;
    }

    setState('loading');
    attachScriptListeners(findExistingAppScript());

    window.setTimeout(() => {
      attemptRecovery('initial-check');
    }, POST_LOAD_CHECK_DELAY_MS);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.instafeed-kind-toggle__button');
    if (!button) return;

    const root = getFeedRoot();
    const kind = button.dataset.instafeedKind;
    if (!root || !FEED_KINDS.includes(kind) || button.disabled) return;

    setActiveKind(root, kind);
    applyFeedFilter(root, { resetScroll: true });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const button = event.target.closest('.instafeed-kind-toggle__button');
    if (!button) return;

    const root = getFeedRoot();
    const toggle = button.closest('.instafeed-kind-toggle');
    if (!root || !toggle) return;

    const currentIndex = FEED_KINDS.indexOf(button.dataset.instafeedKind);
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextKind = FEED_KINDS[(currentIndex + direction + FEED_KINDS.length) % FEED_KINDS.length];
    const nextButton = toggle.querySelector(`[data-instafeed-kind="${nextKind}"]`);

    if (!nextButton || nextButton.disabled) return;

    event.preventDefault();
    setActiveKind(root, nextKind);
    applyFeedFilter(root, { resetScroll: true });
    nextButton.focus();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInstafeedRecovery);
  } else {
    initInstafeedRecovery();
  }

  document.addEventListener('shopify:section:load', initInstafeedRecovery);
  window.addEventListener('resize', () => {
    scheduleLayoutNormalization(getFeedRoot(), POST_LOAD_CHECK_DELAY_MS);
  });

})();
